import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ShieldAlert, Sparkles, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;          // poll every 60s
const INITIAL_DELAY_MS = 5_000;  // wait 5s after mount before first poll (let app settle)

const SystemUpdateContext = createContext(null);

// Severity → toast styling. Kept outside the component so it isn't recreated each render.
const SEVERITY_META = {
  info:     { duration: Infinity, className: 'border-[#002FA7]/30 bg-white',    Icon: Sparkles,     iconColor: 'text-[#002FA7]' },
  warning:  { duration: Infinity, className: 'border-[#FF9500]/40 bg-[#FFF8F0]', Icon: AlertTriangle, iconColor: 'text-[#FF9500]' },
  critical: { duration: Infinity, className: 'border-[#FF3B30]/50 bg-[#FFF5F5]', Icon: ShieldAlert,  iconColor: 'text-[#FF3B30]' },
};

/**
 * Watches the backend's `build_id`. On first successful poll the id is cached;
 * on any subsequent change a persistent toast appears with a one-click
 * "Update now" button that hard-reloads the browser to pick up the new bundle.
 *
 * Admins can trigger a broadcast with `POST /api/system/broadcast-update`,
 * which rotates the `build_id` server-side and attaches an optional `notice`
 * (message + severity). If a notice is present, the toast uses its copy.
 *
 * Notes:
 * - Polling is unauthenticated — the endpoint is public so expired sessions
 *   still get the nudge to reload + re-auth.
 * - One toast at a time: the id is stable ("kstreak-system-update") so repeat
 *   polls don't stack duplicates.
 */
export const SystemUpdateProvider = ({ children }) => {
  const initialBuildIdRef = useRef(null);
  const shownForBuildIdRef = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const handleReload = useCallback(() => {
    try {
      // Best-effort cache bust — service-worker caches are the usual culprit.
      if ('caches' in window) {
        window.caches.keys().then(keys => keys.forEach(k => window.caches.delete(k))).catch(() => {});
      }
    } catch { /* noop */ }
    // Tiny delay so the toast animation finishes before nav.
    setTimeout(() => window.location.reload(), 120);
  }, []);

  const showUpdateToast = useCallback((notice) => {
    const severity = notice?.severity || 'info';
    const meta = SEVERITY_META[severity] || SEVERITY_META.info;
    const defaultMsg = 'A new version of KStreak is available. Reload to get the latest features and fixes.';
    const title = severity === 'critical'
      ? 'Critical update required'
      : severity === 'warning'
        ? 'Update recommended'
        : 'Update available';
    const message = notice?.message?.trim() || defaultMsg;

    toast.custom((t) => (
      <div
        className={`flex items-start gap-3 p-3.5 rounded-lg border shadow-lg w-[380px] ${meta.className}`}
        data-testid="system-update-toast"
      >
        <div className="mt-0.5 flex-shrink-0">
          <meta.Icon className={`w-5 h-5 ${meta.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A]">{title}</p>
          <p className="text-xs text-[#4B5563] mt-0.5 leading-relaxed">{message}</p>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={() => { handleReload(); toast.dismiss(t); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-medium transition-colors"
              data-testid="system-update-reload-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Update now
            </button>
            {severity !== 'critical' && (
              <button
                onClick={() => toast.dismiss(t)}
                className="px-2 py-1.5 text-xs text-[#9CA3AF] hover:text-[#4B5563]"
                data-testid="system-update-dismiss-btn"
              >
                Later
              </button>
            )}
          </div>
        </div>
      </div>
    ), {
      id: 'kstreak-system-update',
      duration: meta.duration,
      dismissible: severity !== 'critical',
    });
  }, [handleReload]);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/system/version`, { timeout: 8000 });
      const buildId = r.data?.build_id || '';
      if (!buildId) return;

      // First poll — capture baseline, don't alert.
      if (initialBuildIdRef.current === null) {
        initialBuildIdRef.current = buildId;
        return;
      }
      // Build changed since we loaded this tab → new version.
      if (buildId !== initialBuildIdRef.current && shownForBuildIdRef.current !== buildId) {
        shownForBuildIdRef.current = buildId;
        setUpdateAvailable(true);
        showUpdateToast(r.data?.notice);
      }
    } catch { /* silent — network blips shouldn't spam users */ }
  }, [showUpdateToast]);

  useEffect(() => {
    const t0 = setTimeout(poll, INITIAL_DELAY_MS);
    const interval = setInterval(poll, POLL_MS);
    // Also re-check when tab regains focus — long-idle tabs get fresh versions fast.
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => {
      clearTimeout(t0);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [poll]);

  return (
    <SystemUpdateContext.Provider value={{ updateAvailable, reload: handleReload }}>
      {children}
    </SystemUpdateContext.Provider>
  );
};

export const useSystemUpdate = () => {
  const ctx = useContext(SystemUpdateContext);
  if (!ctx) throw new Error('useSystemUpdate must be used within SystemUpdateProvider');
  return ctx;
};
